import {
  Alert,
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import {
  ActionIconButton,
  CustomAutoComplete,
  CustomButton,
  CustomTextField,
  DrawerFormSection,
} from '../../../../common/components';
import {
  CHECK_KIND_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  RETENTION_TYPE_OPTIONS,
} from '../../constants/supplierLedgerUiConstants';
import { formatCurrencyAmount } from '../../utils/supplierLedgerFormatters';
import {
  AmountPanel,
  FieldSelect,
} from '../SupplierLedgerShared';
import PaymentOrderProviderHeader from './PaymentOrderProviderHeader';

const RetentionsEditor = ({ controller }) => {
  const {
    activePaymentForm,
    addRetention,
    removeRetention,
    updateRetention,
  } = controller;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800 }}>Retenciones</Typography>
        <CustomButton variant="outlined" startIcon={<AddIcon />} onClick={addRetention} disabled={!activePaymentForm.pay_now} sx={{ height: 34 }}>
          Agregar
        </CustomButton>
      </Box>
      {(activePaymentForm.retentions || []).length ? activePaymentForm.retentions.map((row) => (
        <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 120px 42px' }, gap: 1, mb: 1 }}>
          <FieldSelect label="Tipo" name={`retention_type_${row.id}`} value={row.retention_type} onChange={(event) => updateRetention(row.id, 'retention_type', event.target.value)} options={RETENTION_TYPE_OPTIONS} />
          <CustomTextField label="Certificado" value={row.certificate_number} onChange={(event) => updateRetention(row.id, 'certificate_number', event.target.value)} />
          <CustomTextField label="Importe" value={row.amount} onChange={(event) => updateRetention(row.id, 'amount', event.target.value)} />
          <Box sx={{ mt: '12px' }}>
            <ActionIconButton variant="delete" onClick={() => removeRetention(row.id)} />
          </Box>
        </Box>
      )) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Sin retenciones cargadas.</Typography>
      )}
    </Box>
  );
};

const ChecksEditor = ({ controller }) => {
  const {
    activePaymentForm,
    addCheck,
    bankAccounts,
    removeCheck,
    updateCheck,
  } = controller;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800 }}>Cheques</Typography>
        <CustomButton variant="outlined" startIcon={<AddIcon />} onClick={addCheck} disabled={!activePaymentForm.pay_now} sx={{ height: 34 }}>
          Agregar
        </CustomButton>
      </Box>
      {(activePaymentForm.checks || []).length ? activePaymentForm.checks.map((row) => (
        <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 120px 42px' }, gap: 1, mb: 1 }}>
          <FieldSelect label="Tipo" name={`check_kind_${row.id}`} value={row.check_kind} onChange={(event) => updateCheck(row.id, 'check_kind', event.target.value)} options={CHECK_KIND_OPTIONS} />
          <CustomAutoComplete
            label="Banco"
            value={row.uuid_bank_account}
            onChange={(event) => updateCheck(row.id, 'uuid_bank_account', event.target.value)}
            options={bankAccounts.map((account) => ({ value: account.uuid, label: `${account.bank_name || account.name || 'Banco'} ${account.account_number || ''}`.trim() }))}
            disabled={row.check_kind !== 'own'}
          />
          <CustomTextField label="Número" value={row.check_number} onChange={(event) => updateCheck(row.id, 'check_number', event.target.value)} />
          <CustomTextField label="Importe" value={row.amount} onChange={(event) => updateCheck(row.id, 'amount', event.target.value)} />
          <Box sx={{ mt: '12px' }}>
            <ActionIconButton variant="delete" onClick={() => removeCheck(row.id)} />
          </Box>
          <CustomTextField label="Vencimiento" type="date" value={row.due_date} onChange={(event) => updateCheck(row.id, 'due_date', event.target.value)} shrinkLabel sx={{ gridColumn: { xs: '1', md: 'span 2' } }} />
        </Box>
      )) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Sin cheques cargados.</Typography>
      )}
    </Box>
  );
};

const PaymentFields = ({ controller }) => {
  const {
    activeChecksTotal,
    activeNetPayable,
    activePaymentForm,
    activeProviderOrderGroups,
    activeProviderTotal,
    activeRetentionsTotal,
    fundAccounts,
    handlePaymentChange,
    movementTypes,
  } = controller;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' }, gap: 1.25, mb: 1.5 }}>
      <FieldSelect
        label="Medio"
        name="payment_method"
        value={activePaymentForm.payment_method}
        onChange={handlePaymentChange}
        options={PAYMENT_METHOD_OPTIONS}
        sx={{ gridColumn: { xs: '1', md: 'span 3' } }}
      />
      {['transfer', 'cash'].includes(activePaymentForm.payment_method) && (
        <>
          <CustomAutoComplete
            label="Banco/Caja"
            name="fund_account_id"
            value={activePaymentForm.fund_account_id}
            onChange={handlePaymentChange}
            options={fundAccounts
              .filter((account) => activePaymentForm.payment_method === 'cash' ? account.fund_type === 'cash' : account.fund_type === 'bank')
              .map((account) => ({
                value: account.uuid,
                label: account.label || account.name || account.account_number || account.uuid,
              }))}
            disabled={!activePaymentForm.pay_now}
            sx={{ gridColumn: { xs: '1', md: 'span 4' } }}
          />
          <CustomAutoComplete
            label="Tipo movimiento"
            name="uuid_movement_type"
            value={activePaymentForm.uuid_movement_type}
            onChange={handlePaymentChange}
            options={movementTypes.map((type) => ({ value: type.uuid, label: type.name || type.description || type.uuid }))}
            disabled={!activePaymentForm.pay_now}
            sx={{ gridColumn: { xs: '1', md: 'span 3' } }}
          />
          <CustomTextField
            label="Comprobante pago"
            name="voucher_number"
            value={activePaymentForm.voucher_number}
            onChange={handlePaymentChange}
            disabled={!activePaymentForm.pay_now}
            sx={{ gridColumn: { xs: '1', md: 'span 2' } }}
          />
          <CustomTextField
            label="Vencimiento"
            type="date"
            name="due_date"
            value={activePaymentForm.due_date}
            onChange={handlePaymentChange}
            disabled={!activePaymentForm.pay_now}
            shrinkLabel
            sx={{ gridColumn: { xs: '1', md: 'span 3' } }}
          />
        </>
      )}
      <AmountPanel label="Total aplicado" value={formatCurrencyAmount(activeProviderTotal, activeProviderOrderGroups[0]?.currency || 'ARS')} sx={{ gridColumn: { xs: '1', md: 'span 3' } }} />
      <AmountPanel label="Retenciones" value={formatCurrencyAmount(activeRetentionsTotal, activeProviderOrderGroups[0]?.currency || 'ARS')} sx={{ gridColumn: { xs: '1', md: 'span 3' } }} />
      <AmountPanel label="Cheques" value={formatCurrencyAmount(activeChecksTotal, activeProviderOrderGroups[0]?.currency || 'ARS')} sx={{ gridColumn: { xs: '1', md: 'span 3' } }} />
      <AmountPanel label="Neto a pagar" value={formatCurrencyAmount(activeNetPayable, activeProviderOrderGroups[0]?.currency || 'ARS')} sx={{ gridColumn: { xs: '1', md: 'span 3' } }} />
    </Box>
  );
};

const PaymentOrderPaymentSection = ({ controller }) => {
  const {
    activePaymentCanBeRegistered,
    activePaymentForm,
    handlePaymentChange,
  } = controller;

  return (
    <>
      <PaymentOrderProviderHeader controller={controller} />
      <DrawerFormSection title="Medios de pago" grid={false}>
        {!activePaymentCanBeRegistered && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Este proveedor genera más de una OP por moneda/cotización. Generá las OP pendientes y completá el pago desde Órdenes de pago.
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Checkbox
            checked={Boolean(activePaymentForm.pay_now)}
            name="pay_now"
            onChange={handlePaymentChange}
            disabled={!activePaymentCanBeRegistered}
          />
          <Typography variant="body2" sx={{ fontWeight: 800, color: activePaymentForm.pay_now ? 'text.primary' : 'text.secondary' }}>
            Registrar pago ahora
          </Typography>
        </Box>
        <PaymentFields controller={controller} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          <RetentionsEditor controller={controller} />
          <ChecksEditor controller={controller} />
        </Box>
      </DrawerFormSection>
    </>
  );
};

export default PaymentOrderPaymentSection;
